import { z } from 'zod/v3';

export const BriefingFindingCategorySchema = z.enum([
  'cloud_misconfig_pr',
  'github_control_pr',
  'policy_review',
  'stale_evidence_auto',
  'stale_evidence_human',
  'risk_register',
  'auditor_question',
  'consistency_gap',
  'access_review',
  'training_overdue',
  'vendor_renewal',
  'unactionable_scan',
  'policy_claims_unverified',
  'tests_uncovered',
  'compliance_dangling',
  'ai_governance',
]);
export type BriefingFindingCategory = z.infer<typeof BriefingFindingCategorySchema>;

export const BriefingDraftedArtifactTypeSchema = z.enum([
  'pr',
  'policy',
  'evidence',
  'dm',
  'risk',
  'reply',
  'questionnaire',
]);
export type BriefingDraftedArtifactType = z.infer<typeof BriefingDraftedArtifactTypeSchema>;

export const BriefingDraftedArtifactSchema = z.object({
  type: BriefingDraftedArtifactTypeSchema,
  ref: z.string(),
  summary: z.string().optional(),
});
export type BriefingDraftedArtifact = z.infer<typeof BriefingDraftedArtifactSchema>;

export const BriefingDetailTargetSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('test'), id: z.string() }),
  z.object({ type: z.literal('risk'), id: z.string() }),
  z.object({ type: z.literal('policy'), slug: z.string() }),
  z.object({ type: z.literal('vendor'), id: z.string() }),
  z.object({ type: z.literal('training'), programId: z.string() }),
  z.object({ type: z.literal('audit_cycle'), cycleId: z.string() }),
  z.object({ type: z.literal('task'), category: z.string(), id: z.string() }),
]);
export type BriefingDetailTarget = z.infer<typeof BriefingDetailTargetSchema>;

export const BriefingFindingSchema = z.object({
  id: z.string(),
  severity: z.enum(['critical', 'warning', 'info']),
  title: z.string(),
  controlRef: z.string(),
  description: z.string(),
  actionLabel: z.string().optional(),
  category: BriefingFindingCategorySchema.optional(),
  draftedArtifact: BriefingDraftedArtifactSchema.optional(),
  assignee: z.object({
    id: z.string(),
    name: z.string(),
    isAgent: z.boolean(),
  }).optional(),
  dueDate: z.string().optional(),
  detailTarget: BriefingDetailTargetSchema.optional(),
  programId: z.string().optional(),
  framework: z.string().optional(),
});
export type BriefingFinding = z.infer<typeof BriefingFindingSchema>;

export const BriefingDoneItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  controlRef: z.string(),
  description: z.string(),
  completedAt: z.string(),
  changeRef: z.object({
    type: z.enum(['pr', 'evidence', 'policy']),
    id: z.string(),
  }).optional(),
  testId: z.string().optional(),
});
export type BriefingDoneItem = z.infer<typeof BriefingDoneItemSchema>;

export const BriefingProgramSummarySchema = z.object({
  programId: z.string(),
  framework: z.string(),
  overall: z.number(),
  delta: z.number().optional(),
});
export type BriefingProgramSummary = z.infer<typeof BriefingProgramSummarySchema>;

export const BriefingTaskAttentionItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  title: z.string(),
  assigneeName: z.string().nullable(),
  overdueDays: z.number().nullable(),
  blocked: z.boolean(),
  unassigned: z.boolean(),
  highPriority: z.boolean(),
  url: z.string(),
});
export type BriefingTaskAttentionItem = z.infer<typeof BriefingTaskAttentionItemSchema>;

export const BriefingTaskOverviewSchema = z.object({
  active: z.number(),
  overdue: z.number(),
  dueToday: z.number(),
  createdLast7d: z.number(),
  resolvedLast7d: z.number(),
  veraHandling: z.number(),
  veraRunning: z.number(),
  needsHuman: z.number(),
  unassigned: z.number(),
  attention: z.array(BriefingTaskAttentionItemSchema),
  attentionTotal: z.number(),
  boardUrl: z.string(),
});
export type BriefingTaskOverview = z.infer<typeof BriefingTaskOverviewSchema>;
